import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bbox } from '@turf/turf';
import clsx from 'clsx';
import { Marker, Source, Layer } from 'react-map-gl';
import { Popup as MapboxPopup } from 'mapbox-gl';
import { makeStyles } from '@mui/styles';
import { Checkbox, FormControlLabel } from '@mui/material';

import palette from '../styles/palette';
import { getMinutesOfDay, getTime } from '../../utils/date';
import DriverLocationPin from '../icons/DriverPin';
import TimeRangeSliderWithEvents from '../TimeRangeSlider';
import { getDriverLocationForDepartedRoute } from '../../services/routes';
import usePolling from '../../hooks/usePolling';
import useLocationQueryParams from '../../hooks/useLocationQueryParams';
import { handleErrorNotification } from '../../store/actions/notifications';
import { useOverlay } from '../Overlay';
import usePrevious from '../../hooks/usePrevious';

const useStyles = makeStyles(({ typography }) => ({
  actionContainer: {
    position: 'absolute',
    top: typography.pxToRem(25),
    right: typography.pxToRem(190),
    display: 'flex',
    alignItems: 'center',
    zIndex: 3,
  },
  info: {
    display: 'flex',
    background: 'rgba(0, 0, 0, .67)',
    padding: '4px 5px',
    color: palette.typography.white.solid,
    borderRadius: '4px',
  },
  title: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      lineHeight: '14px',
      fontSize: '14px',
    },
  },
}));

const DRIVER_ROUTE_SOURCE_ID = 'driver-route-source';
const DRIVER_ROUTE_LAYER_ID = 'driver-route-line';
const DRIVER_POINTS_SOURCE_ID = 'driver-points-source';
const DRIVER_POINTS_LAYER_ID = 'driver-points-circle';

const DriverMapLayer = ({ map, marginTop }) => {
  const styles = useStyles();
  const dispatch = useDispatch();

  const [driverLocations, setDriverLocations] = useState(null);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [driverRouteData, setDriverRouteData] = useState(null);
  const [driverPoints, setDriverPoints] = useState(null);
  const [showDriverLocation, setShowDriverLocation] = useState(false);
  const [range, setRange] = useState([0, 1439]);

  const { routeId, depot } = useLocationQueryParams();
  const { showOverlay, hideOverlay } = useOverlay();
  const prevRouteId = usePrevious(routeId);

  useEffect(() => {
    setDriverLocations(null);
    setShowDriverLocation(false);
  }, [depot, routeId]);

  const getLocations = useCallback(async () => {
    try {
      showOverlay();
      const locations = await getDriverLocationForDepartedRoute([
        depot,
        routeId,
      ]);

      if (!locations || !Array.isArray(locations) || locations.length === 0) {
        setDriverLocations(null);
        setDriverRouteData(null);
        setDriverPoints(null);
        setFilteredLocations([]);
        setShowDriverLocation(false);

        dispatch(
          handleErrorNotification({
            message: 'No driver location data available for this route.',
          })
        );
        return;
      }
      setDriverLocations(locations);
      setShowDriverLocation(true);
    } catch (error) {
      setDriverLocations(null);
      setDriverRouteData(null);
      setDriverPoints(null);
      setFilteredLocations([]);
      setShowDriverLocation(false);

      dispatch(handleErrorNotification(error));
    } finally {
      hideOverlay();
    }
  }, [depot, routeId, dispatch, showOverlay, hideOverlay]);

  const handlePollingSuccess = useCallback((location) => {
    setDriverLocations((prevDriverLocations) => {
      if (
        Array.isArray(prevDriverLocations) &&
        (!prevDriverLocations.length ||
          prevDriverLocations[0].timestamp < location[0].timestamp)
      ) {
        return [location[0], ...prevDriverLocations];
      }
      return prevDriverLocations;
    });
  }, []);

  const handlePollingError = useCallback(
    (error) => {
      dispatch(handleErrorNotification(error));
    },
    [dispatch]
  );

  const handlePollingErrorRetry = useCallback(
    (error, key, config, revalidate, { retryCount }) => {
      if (error.status !== 400 || retryCount >= 5) {
        return;
      }
      setTimeout(() => revalidate({ retryCount }), 60000);
    },
    []
  );

  usePolling(
    showDriverLocation &&
      driverLocations &&
      driverLocations.length &&
      prevRouteId === routeId
      ? [depot, routeId, 1]
      : null,
    getDriverLocationForDepartedRoute,
    {
      refreshInterval: 60000,
      onSuccess: handlePollingSuccess,
      onError: handlePollingError,
      onErrorRetry: handlePollingErrorRetry,
    }
  );

  useEffect(() => {
    if (driverLocations && driverLocations.length) {
      const filteredLocations = driverLocations.filter(
        (location) =>
          getMinutesOfDay(location.timestamp) >= range[0] &&
          getMinutesOfDay(location.timestamp) <= range[1]
      );
      setFilteredLocations(filteredLocations);
    }
  }, [driverLocations, range]);

  const generateStopGeoJSON = (stops) => ({
    type: 'FeatureCollection',
    features: stops.map((stop) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [stop.longitude, stop.latitude],
      },
      properties: {
        timestamp: stop.timestamp,
      },
    })),
  });

  const fetchRoute = async (coordinates, callbackFn) => {
    const MAX_WAYPOINTS = 200;
    const accessToken = import.meta.env.VITE_MAPBOX_API_KEY;
    const baseUrl = 'https://api.mapbox.com/directions/v5/mapbox/driving';

    const getSegmentRoute = async (segment) => {
      const coordsStr = segment.map((c) => c.join(',')).join(';');
      const url = `${baseUrl}/${coordsStr}?geometries=geojson&overview=full&access_token=${accessToken}`;
      try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.routes?.length > 0) {
          return data.routes[0].geometry.coordinates;
        }
        return [];
      } catch (error) {
        return [];
      }
    };

    const segments = [];
    for (let i = 0; i < coordinates.length - 1; i += MAX_WAYPOINTS - 1) {
      segments.push(coordinates.slice(i, i + MAX_WAYPOINTS));
    }

    const allSegmentCoords = await Promise.all(
      segments.map((segment, index) =>
        getSegmentRoute(segment).then((coords) => ({ index, coords }))
      )
    );

    allSegmentCoords.sort((a, b) => a.index - b.index);
    const fullCoords = allSegmentCoords.reduce((acc, item, index) => {
      if (index === 0) {
        return item.coords;
      }
      return [...acc, ...item.coords.slice(1)];
    }, []);

    const routeGeoJson = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: fullCoords,
      },
    };
    callbackFn(routeGeoJson);
  };

  useEffect(() => {
    if (filteredLocations.length) {
      const coordinates = filteredLocations.map(({ longitude, latitude }) => [
        longitude,
        latitude,
      ]);

      (coordinates.length >= 2 &&
        fetchRoute(coordinates, setDriverRouteData)) ||
        setDriverRouteData(null);
      setDriverPoints(generateStopGeoJSON(filteredLocations));
    } else {
      setDriverRouteData(null);
      setDriverPoints(null);
    }
  }, [filteredLocations]);

  const handleOnCenterMap = useCallback(() => {
    const fitBoundsOptions = {
      padding: { top: 80, bottom: 80, left: 150, right: 5 },
    };
    if (!driverPoints) {
      return;
    }
    const fitBounds = bbox(driverPoints);
    showDriverLocation && map && map.fitBounds(fitBounds, fitBoundsOptions);
  }, [map, driverPoints, showDriverLocation]);

  const handleCircleOnMouseEnter = useCallback(
    (e) => {
      map.getCanvas().style.cursor = 'pointer';
      const timestamp = e.features[0]?.properties?.timestamp;
      const circleCoordinates = e.features[0].geometry.coordinates || e.lngLat;
      if (timestamp) {
        e.target.popup = new MapboxPopup({
          anchor: 'center',
          closeButton: false,
          offset: { center: [-10, -25] },
        })
          .setLngLat(circleCoordinates)
          .setText(getTime(timestamp))
          .addTo(map);
      }
    },
    [map]
  );

  const handleCircleOnMouseLeave = useCallback(
    (e) => {
      map.getCanvas().style.cursor = '';
      if (e.target.popup) {
        e.target.popup.remove();
        e.target.popup = null;
      }
    },
    [map]
  );

  useEffect(() => {
    if (!map || !driverPoints) {
      return undefined;
    }
    map.on('mouseenter', DRIVER_POINTS_LAYER_ID, handleCircleOnMouseEnter);
    map.on('mouseleave', DRIVER_POINTS_LAYER_ID, handleCircleOnMouseLeave);
    return () => {
      map.off('mouseenter', DRIVER_POINTS_LAYER_ID, handleCircleOnMouseEnter);
      map.off('mouseleave', DRIVER_POINTS_LAYER_ID, handleCircleOnMouseLeave);
    };
  }, [map, driverPoints, handleCircleOnMouseEnter, handleCircleOnMouseLeave]);

  useEffect(() => {
    if (driverPoints) {
      handleOnCenterMap();
    }
  }, [handleOnCenterMap, driverPoints]);

  const currentDriverCoordinates = useMemo(() => {
    if (driverLocations && driverLocations.length) {
      return [driverLocations[0].longitude, driverLocations[0].latitude];
    }
    return null;
  }, [driverLocations]);

  const locationEventTime = useMemo(
    () =>
      (driverLocations &&
        driverLocations.map((location) =>
          getMinutesOfDay(location.timestamp)
        )) ||
      [],
    [driverLocations]
  );

  const onTimeRangeChange = useCallback(
    (newValue) => {
      setRange(newValue);
    },
    [setRange]
  );

  const handleShowDriverLocation = useCallback(async () => {
    if (!showDriverLocation) {
      await getLocations();
    } else {
      setShowDriverLocation((prev) => !prev);
    }
  }, [showDriverLocation, getLocations]);

  return (
    <>
      <div
        className={clsx('driver-map-layer-control', styles.actionContainer)}
        style={{
          top: marginTop + 15,
          marginTop: !showDriverLocation ? '10px' : 0,
          transition: 'top .2s',
        }}
      >
        <div className={styles.info}>
          {showDriverLocation && (
            <TimeRangeSliderWithEvents
              onTimeRangeChange={onTimeRangeChange}
              eventTimes={locationEventTime}
              timeRange={range}
            />
          )}
          <FormControlLabel
            value="original"
            control={
              <Checkbox
                sx={{
                  color: palette.white,
                  '&.Mui-checked': {
                    color: palette.white,
                  },
                }}
                inputProps={{ 'aria-label': 'primary checkbox' }}
                onChange={handleShowDriverLocation}
                checked={showDriverLocation}
              />
            }
            label="Show Driver Route"
            labelPlacement="start"
            className={styles.title}
          />
        </div>
      </div>
      {showDriverLocation && (
        <>
          {driverRouteData && (
            <Source
              id={DRIVER_ROUTE_SOURCE_ID}
              type="geojson"
              data={driverRouteData}
            >
              <Layer
                id={DRIVER_ROUTE_LAYER_ID}
                type="line"
                layout={{
                  'line-join': 'round',
                  'line-cap': 'round',
                }}
                paint={{
                  'line-color': palette.dpdRed,
                  'line-width': 5,
                  'line-opacity': 0.5,
                }}
              />
            </Source>
          )}
          {driverPoints && (
            <Source
              id={DRIVER_POINTS_SOURCE_ID}
              type="geojson"
              data={driverPoints}
            >
              <Layer
                id={DRIVER_POINTS_LAYER_ID}
                type="circle"
                paint={{
                  'circle-radius': 5,
                  'circle-color': palette.dpdRed,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': palette.white,
                  'circle-opacity': 0.5,
                }}
              />
            </Source>
          )}
          {currentDriverCoordinates && (
            <Marker
              longitude={currentDriverCoordinates[0]}
              latitude={currentDriverCoordinates[1]}
            >
              <DriverLocationPin />
            </Marker>
          )}
        </>
      )}
    </>
  );
};

export default memo(DriverMapLayer);
